'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  MYCLE_CONTENT_MENU,
  type TCarStoryMenuCode,
  type TMycleContentMenu,
} from './home'
import { CAR_STORY_LAST_VISITED_STORAGE_KEY } from '../lib/storage'

type TLastVisitedMap = Partial<Record<TMycleContentMenu, string>>

type TLatestPublishedAt = Record<TCarStoryMenuCode, string> | undefined

const readLastVisited = (): TLastVisitedMap => {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(CAR_STORY_LAST_VISITED_STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as TLastVisitedMap
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

const writeLastVisited = (next: TLastVisitedMap) => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(
      CAR_STORY_LAST_VISITED_STORAGE_KEY,
      JSON.stringify(next)
    )
  } catch {
    /* localStorage 사용 불가 환경에서는 무시 */
  }
}

/**
 * 탭별 새 콘텐츠 여부(hasNew) 계산 훅.
 *
 * - localStorage 에 저장된 "마지막으로 본 latestPublishedAt" 값과 현재 API 값을 비교
 * - 저장값이 없거나(첫 방문) API 값이 더 최신이면 hasNew = true
 * - markVisited 호출 시 현재 API 값을 그대로 저장 (시계 오차 회피)
 */
const useCarStoryHasNew = (latestPublishedAt: TLatestPublishedAt) => {
  // SSR/CSR hydration mismatch 회피를 위해 첫 렌더는 빈 상태, 클라이언트에서 effect로 1회 로드
  const [lastVisited, setLastVisited] = useState<TLastVisitedMap>({})

  useEffect(function loadLastVisitedFromStorage() {
    setLastVisited(readLastVisited())
  }, [])

  // 탭별 hasNew 파생값은 latestPublishedAt / lastVisited 변경 시에만 재계산
  const hasNewByMenu = useMemo(() => {
    const compute = (menu: TMycleContentMenu): boolean => {
      const apiValue = latestPublishedAt?.[menu]
      if (!apiValue) return false
      const stored = lastVisited[menu]
      if (!stored) return true
      return apiValue > stored
    }
    return {
      [MYCLE_CONTENT_MENU.CAR]: compute(MYCLE_CONTENT_MENU.CAR),
      [MYCLE_CONTENT_MENU.DRIVE]: compute(MYCLE_CONTENT_MENU.DRIVE),
      [MYCLE_CONTENT_MENU.NOW]: compute(MYCLE_CONTENT_MENU.NOW),
    }
  }, [latestPublishedAt, lastVisited])

  const markVisited = useCallback(
    (menu: TMycleContentMenu) => {
      const apiValue = latestPublishedAt?.[menu]
      if (!apiValue) return
      setLastVisited(prev => {
        if (prev[menu] === apiValue) return prev
        const next = { ...prev, [menu]: apiValue }
        writeLastVisited(next)
        return next
      })
    },
    [latestPublishedAt]
  )

  return {
    hasNewCar: hasNewByMenu[MYCLE_CONTENT_MENU.CAR],
    hasNewDrive: hasNewByMenu[MYCLE_CONTENT_MENU.DRIVE],
    hasNewNow: hasNewByMenu[MYCLE_CONTENT_MENU.NOW],
    markVisited,
  }
}

export default useCarStoryHasNew
